import React, { useState, useEffect } from 'react';
import { Stethoscope, Plus, X, Phone, Users, UserCheck, Search, Edit, Trash2, Filter, Printer, Mail, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const Doctors = () => {
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // 🟢 NEW STATE: Tracks which doctor card is expanded to show all patients
  const [expandedDocId, setExpandedDocId] = useState(null);

  const [form, setForm] = useState({ 
      id: null, name: '', phone: '', fax: '', email: '', address: '', category: '' 
  });
  const [isEditing, setIsEditing] = useState(false);

  // ✅ LOCALHOST URL
  const API_URL = 'https://unifyrxcrm.com/AG-BACKEND/index.php';
  const CATEGORIES = [
    "Allergy & Immunology", "Anesthesiology", "Cardiology", "Cardiothoracic Surgery",
    "Child & Adolescent Psychiatry", "Colorectal Surgery", "Critical Care Medicine",
    "Dermatology", "Diagnostic Radiology", "Emergency Medicine", "Endocrinology",
    "Family Medicine", "Gastroenterology", "General Surgery", "Geriatric Medicine",
    "Gynecologic Oncology", "Hematology", "Hyperbaric Medicine", "Infectious Disease",
    "Internal Medicine", "Interventional Cardiology", "Maternal-Fetal Medicine",
    "Medical Oncology", "Neonatology", "Nephrology", "Neurology", "Neurosurgery",
    "Nuclear Medicine", "Obstetrics & Gynecology", "Occupational Medicine", "Ophthalmology",
    "Orthopedic Surgery", "Otolaryngology (ENT)", "Pain Medicine", "Pathology",
    "Pediatric Cardiology", "Pediatric Endocrinology", "Pediatric Gastroenterology",
    "Pediatric Hematology & Oncology", "Pediatric Nephrology", "Pediatric Pulmonology",
    "Pediatrics", "Physical Medicine & Rehabilitation (PM&R)", "Plastic Surgery",
    "Psychiatry", "Pulmonology", "Radiation Oncology", "Reproductive Endocrinology & Infertility",
    "Rheumatology", "Surgical Oncology", "Sports Medicine", "Trauma Surgery",
    "Urology"
  ];

  // 🟢 HELPER: Toggles the patient list view
  const toggleExpand = (id) => {
    setExpandedDocId(expandedDocId === id ? null : id);
  };

  const fetchDoctors = async () => {
    try {
        const res = await axios.get(`${API_URL}?action=get_doctors&_t=${Date.now()}`);
        if (Array.isArray(res.data)) {
            setDoctors(res.data);
        } else {
            setDoctors([]);
        }
        setLoading(false);
    } catch (error) { console.error(error); setLoading(false); }
  };

  useEffect(() => { fetchDoctors(); }, []);

  const filteredDoctors = doctors.filter(doc => {
    const sTerm = searchTerm.toLowerCase();
    const nameMatch = doc.name ? doc.name.toLowerCase().includes(sTerm) : false;
    const phoneMatch = doc.phone ? doc.phone.includes(sTerm) : false;
    
    const matchesSearch = nameMatch || phoneMatch;
    const matchesCategory = selectedCategory === '' || doc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleAddClick = () => {
    setForm({ id: null, name: '', phone: '', fax: '', email: '', address: '', category: '' });
    setIsEditing(false);
    setShowModal(true);
  };

  const handleEditClick = (doc) => {
    setForm({ 
        id: doc.id, 
        name: doc.name || '', 
        phone: doc.phone || '', 
        fax: doc.fax || '', 
        email: doc.email || '', 
        address: doc.address || '', 
        category: doc.category || '' 
    });
    setIsEditing(true);
    setShowModal(true);
  };

  const handleDeleteClick = async (id) => {
    if (window.confirm("Are you sure you want to delete this doctor?")) {
        try {
            await axios.post(`${API_URL}?action=delete_doctor`, { id });
            fetchDoctors();
        } catch (error) { console.error(error); alert("Error deleting doctor"); }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const action = isEditing ? 'update_doctor' : 'add_doctor';
    try {
        await axios.post(`${API_URL}?action=${action}`, form);
        alert(isEditing ? "Doctor Updated!" : "Doctor Added!");
        setShowModal(false);
        fetchDoctors();
    } catch (error) { console.error(error); alert("Error saving doctor"); }
  };

  const parsePatients = (patientString) => {
      if (!patientString || typeof patientString !== 'string') return [];

      // Check for old format (Fallback)
      if (!patientString.includes('***')) {
          return patientString.split(',').map(name => ({
              name: name.trim(),
              id: null 
          }));
      }

      return patientString.split('||').map(p => {
          const parts = p.split('***');
          return {
              name: parts[0] ? parts[0].trim() : "Unknown",
              id: parts[1] ? parts[1].trim() : null
          };
      });
  };

  return (
    <div className="p-4">
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <h2 className="text-2xl font-bold text-gray-700 flex items-center gap-2">
          <Stethoscope className="text-teal-600" /> Doctors Directory
        </h2>
        
        <div className="flex flex-wrap gap-3 items-center w-full md:w-auto">
            <div className="relative flex-grow md:flex-grow-0">
                <Filter className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <select 
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 w-full md:w-56 bg-white"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                >
                    <option value="">All Categories</option>
                    {CATEGORIES.map((cat, index) => (
                        <option key={index} value={cat}>{cat}</option>
                    ))}
                </select>
            </div>

            <div className="relative flex-grow md:flex-grow-0">
                <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input 
                    type="text" 
                    placeholder="Search Name or Phone..." 
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 w-full md:w-56"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <button onClick={handleAddClick} className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 flex items-center gap-2 shadow whitespace-nowrap">
                <Plus size={16} /> Add Doctor
            </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? <p className="col-span-3 text-center text-gray-500">Loading directory...</p> : 
         filteredDoctors.length === 0 ? <p className="col-span-3 text-center text-gray-500">No doctors found.</p> : 
         filteredDoctors.map((doc) => {
            
            const patientList = parsePatients(doc.patients_handled);
            const isExpanded = expandedDocId === doc.id;

            return (
                <div key={doc.id} className="bg-white p-5 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition flex flex-col justify-between relative group h-full">
                    
                    <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                        <button onClick={() => handleEditClick(doc)} className="text-gray-400 hover:text-teal-600 bg-white p-1 rounded shadow-sm border">
                            <Edit size={16} />
                        </button>
                        <button onClick={() => handleDeleteClick(doc.id)} className="text-gray-400 hover:text-red-600 bg-white p-1 rounded shadow-sm border">
                            <Trash2 size={16} />
                        </button>
                    </div>

                    <div>
                        <div className="flex items-start gap-3 mb-3">
                            <div className="bg-teal-100 p-2 rounded-full text-teal-600 mt-1 flex-shrink-0">
                                <Stethoscope size={20} />
                            </div>
                            <div className="min-w-0">
                                <h3 className="font-bold text-lg text-gray-800 leading-tight truncate">{doc.name}</h3>
                                {doc.category && (
                                    <span className="bg-purple-100 text-purple-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-purple-200 inline-block mt-1 truncate max-w-full">
                                        {doc.category}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="space-y-2 mb-4">
                            <div className="flex gap-2 text-sm text-gray-600">
                                <div className="w-1/2 flex items-center gap-2 truncate" title="Phone">
                                    <Phone size={14} className="text-teal-500 flex-shrink-0"/> {doc.phone || '-'}
                                </div>
                                <div className="w-1/2 flex items-center gap-2 truncate" title="Fax">
                                    <Printer size={14} className="text-gray-400 flex-shrink-0"/> {doc.fax || '-'}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600 truncate" title={doc.email}>
                                <Mail size={14} className="text-red-400 flex-shrink-0"/> {doc.email || '-'}
                            </div>
                            <div className="flex items-start gap-2 text-sm text-gray-600">
                                <MapPin size={14} className="text-green-500 mt-0.5 flex-shrink-0"/> 
                                <span className="line-clamp-2 text-xs">{doc.address || 'No address listed'}</span>
                            </div>
                        </div>
                    </div>

                    {/* ✅ UPDATED: Expandable Patients Section */}
                    <div className="bg-gray-50 rounded p-3 border border-gray-100 mt-auto transition-all duration-300">
                        <div className="flex justify-between items-center mb-2">
                            <p className="text-[10px] font-bold text-gray-400 uppercase flex items-center gap-1">
                                <Users size={12} /> Patients Handled
                            </p>
                            {patientList.length > 0 && (
                                <span className="text-[10px] text-gray-400 bg-gray-200 px-1.5 rounded-full">
                                    {patientList.length}
                                </span>
                            )}
                        </div>
                        
                        {patientList.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                                {/* Conditional Slicing based on Expanded State */}
                                {(isExpanded ? patientList : patientList.slice(0, 3)).map((p, i) => (
                                    <span 
                                        key={i} 
                                        // Only navigate if ID exists
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if(p.id) navigate(`/patient/${p.id}`);
                                        }}
                                        className={`
                                            bg-white border border-gray-200 text-gray-600 text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 transition
                                            ${p.id ? 'hover:bg-teal-50 hover:text-teal-700 hover:border-teal-300 cursor-pointer' : 'cursor-default opacity-70'}
                                        `}
                                        title={!p.id ? "Patient ID missing" : "View Patient Profile"}
                                    >
                                        <UserCheck size={10} /> {p.name}
                                    </span>
                                ))}
                                
                                {/* Show More Button */}
                                {patientList.length > 3 && !isExpanded && (
                                    <span 
                                        onClick={() => toggleExpand(doc.id)}
                                        className="text-[10px] text-teal-600 font-bold bg-teal-50 border border-teal-100 px-2 py-0.5 rounded-full cursor-pointer hover:bg-teal-100 transition"
                                    >
                                        +{patientList.length - 3} more...
                                    </span>
                                )}

                                {/* Show Less Button */}
                                {patientList.length > 3 && isExpanded && (
                                    <span 
                                        onClick={() => toggleExpand(doc.id)}
                                        className="text-[10px] text-gray-500 font-bold bg-gray-200 border border-gray-300 px-2 py-0.5 rounded-full cursor-pointer hover:bg-gray-300 transition"
                                    >
                                        Show Less
                                    </span>
                                )}
                            </div>
                        ) : (
                            <p className="text-xs text-gray-400 italic">No patients assigned yet.</p>
                        )}
                    </div>
                </div>
            );
        })}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-[500px] p-6 relative max-h-[90vh] overflow-y-auto">
                <button onClick={() => setShowModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X size={24} /></button>
                <h3 className="text-xl font-bold mb-4 text-teal-700">{isEditing ? 'Edit Doctor' : 'Add New Doctor'}</h3>
                
                <form onSubmit={handleSubmit} className="space-y-3">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">Doctor Name</label>
                        <input type="text" className="w-full border p-2 rounded focus:ring-2 focus:ring-teal-500 outline-none" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">Specialization</label>
                        <select className="w-full border p-2 rounded bg-white focus:ring-2 focus:ring-teal-500 outline-none" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                            <option value="">-- Select Category --</option>
                            {CATEGORIES.map((cat, index) => (<option key={index} value={cat}>{cat}</option>))}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 mb-1">Phone Number</label>
                            <input type="text" className="w-full border p-2 rounded focus:ring-2 focus:ring-teal-500 outline-none" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 mb-1">Fax Number</label>
                            <input type="text" className="w-full border p-2 rounded focus:ring-2 focus:ring-teal-500 outline-none" value={form.fax} onChange={e => setForm({...form, fax: e.target.value})} />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">Email Address</label>
                        <input type="email" className="w-full border p-2 rounded focus:ring-2 focus:ring-teal-500 outline-none" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">Office Address</label>
                        <textarea className="w-full border p-2 rounded h-20 resize-none focus:ring-2 focus:ring-teal-500 outline-none" value={form.address} onChange={e => setForm({...form, address: e.target.value})}></textarea>
                    </div>
                    <button type="submit" className="w-full bg-teal-600 text-white font-bold py-2 rounded hover:bg-teal-700 mt-2">
                        {isEditing ? 'Update Doctor' : 'Save Doctor'}
                    </button>
                </form>
            </div>
        </div>
      )}
    </div>
  );
};

export default Doctors;